/**
 * PartnerStatusScreen
 *
 * Partner/donor status — only shown for goals that require it
 * (embryo freezing, IVF).
 */

import React, { useEffect, useRef, useState } from "react";
import { View, Text, Pressable, ScrollView, Animated } from "react-native";
import { Users, UserPlus, User } from "lucide-react-native";
import type { LucideIcon } from "lucide-react-native";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import WarmButton from "@louise/moon-ui/primitives/WarmButton";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { usePlanBuilder } from "../PlanBuilderContext";
import type { PartnerStatus } from "../types";

type Option = {
  id: PartnerStatus;
  labelKey: string;
  detailKey: string;
  icon: LucideIcon;
};

const OPTIONS: Option[] = [
  {
    id: "with-partner",
    labelKey: "plan_builder.partner_status.with_partner_label",
    detailKey: "plan_builder.partner_status.with_partner_detail",
    icon: Users,
  },
  {
    id: "using-donor",
    labelKey: "plan_builder.partner_status.using_donor_label",
    detailKey: "plan_builder.partner_status.using_donor_detail",
    icon: UserPlus,
  },
  {
    id: "solo",
    labelKey: "plan_builder.partner_status.solo_label",
    detailKey: "plan_builder.partner_status.solo_detail",
    icon: User,
  },
];

function OptionButton({
  option,
  label,
  detail,
  isSelected,
  onPress,
}: {
  option: Option;
  label: string;
  detail: string;
  isSelected: boolean;
  onPress: () => void;
}) {
  const scale = useRef(new Animated.Value(1)).current;
  const Icon = option.icon;

  const handlePressIn = () => {
    Animated.spring(scale, {
      toValue: 0.97,
      useNativeDriver: true,
    }).start();
  };

  const handlePressOut = () => {
    Animated.spring(scale, {
      toValue: 1,
      friction: 4,
      useNativeDriver: true,
    }).start();
  };

  return (
    <Pressable
      onPress={onPress}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      accessibilityRole="button"
      accessibilityState={{ selected: isSelected }}
      // Inert testability hook: react-native-web 0.21 no longer forwards
      // accessibilityState.selected to the DOM, so mirror it via the
      // aria-selected prop for deterministic E2E assertions.
      aria-selected={isSelected}
    >
      <Animated.View
        style={{
          transform: [{ scale }],
          flexDirection: "row",
          alignItems: "center",
          paddingHorizontal: 18,
          paddingVertical: 16,
          gap: 14,
          backgroundColor: isSelected
            ? `${EMBER.colors.text}10`
            : "rgba(255, 255, 255, 0.82)",
          borderRadius: 14,
          borderWidth: 1,
          borderColor: isSelected
            ? EMBER.colors.text
            : "rgba(255, 255, 255, 0.9)",
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.04,
          shadowRadius: 8,
        }}
      >
        <Icon
          size={20}
          color={isSelected ? EMBER.colors.text : EMBER.colors.textMid}
          strokeWidth={1.5}
        />
        <View style={{ flex: 1 }}>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 15,
              fontWeight: "500",
              color: isSelected ? EMBER.colors.text : EMBER.colors.text,
            }}
          >
            {label}
          </Text>
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              color: EMBER.colors.textLight,
              marginTop: 1,
            }}
          >
            {detail}
          </Text>
        </View>
        <View
          style={{
            width: 22,
            height: 22,
            borderRadius: 11,
            borderWidth: 2,
            borderColor: isSelected ? EMBER.colors.text : EMBER.colors.border,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {isSelected && (
            <View
              style={{
                width: 12,
                height: 12,
                borderRadius: 6,
                backgroundColor: EMBER.colors.text,
              }}
            />
          )}
        </View>
      </Animated.View>
    </Pressable>
  );
}

type Props = {
  onNext: () => void;
};

export default function PartnerStatusScreen({ onNext }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("PartnerStatusScreen");
  const { state, updateState } = usePlanBuilder();
  const [selected, setSelected] = useState<PartnerStatus | null>(
    state.partnerStatus,
  );
  // The patient record (and therefore the stored partner choice) loads
  // asynchronously, so `state.partnerStatus` is usually null on first render.
  // Hydrate the local selection once it arrives, without clobbering a choice
  // the user has already made this session.
  useEffect(() => {
    if (state.partnerStatus) {
      setSelected((prev) => prev ?? state.partnerStatus);
    }
  }, [state.partnerStatus]);
  const handleSelect = (status: PartnerStatus) => {
    setSelected(status);
    updateState({ partnerStatus: status });
  };

  return (
    <ScrollView
      contentContainerStyle={{ padding: 24, paddingBottom: 40 }}
      showsVerticalScrollIndicator={false}
    >
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 22,
          fontWeight: "700",
          color: EMBER.colors.text,
          lineHeight: 28,
          marginBottom: 8,
        }}
      >
        {t("plan_builder.partner_status.title")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 15,
          fontWeight: "400",
          color: EMBER.colors.textLight,
          lineHeight: 22,
          marginBottom: 24,
        }}
      >
        {t("plan_builder.partner_status.subtitle")}
      </Text>

      <View style={{ gap: 10 }}>
        {OPTIONS.map((option) => (
          <OptionButton
            key={option.id}
            option={option}
            label={t(option.labelKey)}
            detail={t(option.detailKey)}
            isSelected={selected === option.id}
            onPress={() => {
              capturePress(pressLabel(t(option.labelKey)), option.id);
              handleSelect(option.id);
            }}
          />
        ))}
      </View>

      {selected && (
        <View style={{ marginTop: 24 }}>
          <WarmButton
            title={t("plan_builder.continue")}
            onPress={() => onNext()}
          />
        </View>
      )}
    </ScrollView>
  );
}
